import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import { ApiParams } from '../../../common/api/api';
import SelectDialog, {
  SelectDialogSearchField,
  SelectDialogSubmitButton,
  SelectDialogTable
} from '../../../common/dialogs/SelectDialog';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import ProductAutocomplete from '../components/ProductAutocomplete';
import {
  useFindProductDesignInputs,
  useProductEntityPrefix
} from '../Product.hooks';
import {
  BaseProductAssignDialogProps,
  ProductDesignInput
} from '../Product.interface';
import { getProductEntityName } from '../Product.config';

export interface AssignDesignInputDialogProps
  extends Omit<
    BaseProductAssignDialogProps<never, ProductDesignInput>,
    'onAssigned' | 'productId'
  > {
  productId?: number;
  title?: string;
  contentText?: string;
  isLoading?: boolean;
  defaultLimit?: number;
  onSubmit?: (designInputs: ProductDesignInput[]) => void;
}

const AssignDesignInputDialog: React.FC<
  AssignDesignInputDialogProps
> = props => {
  const {
    open,
    selectedItems,
    isLoading = false,
    defaultLimit = 5,
    title = `Assign ${getProductEntityName('designInput')}`,
    contentText = `Search for ${getProductEntityName(
      'designInput'
    )} to assign.`,
    onCancel,
    onError,
    onSubmit
  } = props;
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(defaultLimit);
  const [productId, setProductId] = useState(props.productId);
  const {
    productDesignInputList,
    isLoadingProductDesignInputs,
    loadingProductDesignInputsError,
    findProductDesignInputs
  } = useFindProductDesignInputs(productId || 0);
  const prefix: string = useProductEntityPrefix(
    ProductBoardColumnEntity.DesignInput
  );

  // handle searching
  useEffect(() => {
    const params: ApiParams = { search, page, limit };
    findProductDesignInputs(params);
  }, [search, limit, page, productId, findProductDesignInputs]);

  useEffect(() => {
    if (loadingProductDesignInputsError) {
      onError?.(loadingProductDesignInputsError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingProductDesignInputsError]);

  return (
    <SelectDialog open={open} onClose={onCancel} selectedItems={selectedItems}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{contentText}</DialogContentText>
        <SelectDialogSearchField
          label={getProductEntityName('designInput')}
          onSearch={(value: string) => {
            setPage(0);
            setSearch(value);
          }}
        />
        <Box mt={1} mb={2} className='search-box'>
          <ProductAutocomplete
            productId={productId}
            onChange={product => {
              if (product) {
                setProductId(product.id);
              }
            }}
          />
        </Box>
        <SelectDialogTable
          multiple
          idKey='id'
          columns={[
            {
              key: 'id',
              label: 'ID',
              render: (item: ProductDesignInput) => prefix + item.id
            },
            { key: 'title', label: 'Title', wrap: true },
            {
              key: 'task.status',
              label: 'Status',
              render: (item: ProductDesignInput) => {
                return item.task && <TaskStatusLabel task={item.task} />;
              }
            }
          ]}
          isLoading={isLoadingProductDesignInputs || isLoading}
          dataList={productDesignInputList}
          page={page}
          rowsPerPage={limit}
          onChangePage={page => setPage(page)}
          onChangeRowsPerPage={limit => setLimit(limit)}
        />
        {loadingProductDesignInputsError && (
          <Alert severity='error'>
            Error getting data. Please check your internet connection and try
            again.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <SelectDialogSubmitButton onSubmit={onSubmit}>
          Assign
        </SelectDialogSubmitButton>
      </DialogActions>
    </SelectDialog>
  );
};

export default AssignDesignInputDialog;
